'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Layers, CheckCircle2, XCircle, Ship, Search, ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react';
import clsx, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import toast from 'react-hot-toast';

import Modal from '@/components/ui/modal';
import FleetForm from './fleet-form';
import useVesselsStore from '@/store/vessels';
import { useMutationDeleteFleet, useQueryAllFleet } from '@/api/queries/fleet';
import { Fleet, Vessel } from '@/types/api';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function Panel({ title, description, action, children, className }: any) {
  return (
    <div className={cn("bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm", className)}>
      <div className="flex items-start justify-between p-5 pb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white tracking-tight">{title}</h3>
          {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function StatusPill({ tone, children }: any) {
  const map = {
    success: "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",
    danger: "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400",
  } as const;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full", map[tone as keyof typeof map])}>
      <span className={cn("h-1.5 w-1.5 rounded-full", {
        success: "bg-green-500",
        danger: "bg-red-500",
      }[tone as keyof typeof map])} />
      {children}
    </span>
  );
}

function MiniStat({ icon: Icon, label, value, tone }: any) {
  const toneMap = {
    primary: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
    success: "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",
    warning: "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400",
    info: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
  } as any;
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex items-center gap-3">
      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", toneMap[tone])}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{value}</div>
      </div>
    </div>
  );
}

export default function FleetTable() {
  const vesselStore = useVesselsStore((state) => state);
  const { fleets, setFleets } = vesselStore;

  const { isError: isErrorFleet, data: dataFleet } = useQueryAllFleet();
  const deleteFleetMutation = useMutationDeleteFleet();

  const [q, setQ] = useState("");
  const [modalForm, setOpenModalForm] = useState(false);
  const [selectedFleet, setSelectedFleet] = useState<Fleet | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (dataFleet) {
      setFleets(dataFleet);
    }
    if (isErrorFleet) {
      toast.error('Terjadi kesalahan pada pengambilan data fleet, harap hubungi Administrator');
    }
  }, [dataFleet, isErrorFleet, setFleets]);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const deleteFleet = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this fleet?')) {
      toast.promise(deleteFleetMutation.mutateAsync({ id }), {
        loading: 'Deleting fleet...',
        success: 'Successfully deleted!',
        error: (err) => `Could not delete. ${err?.message}`,
      });
    }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return fleets;
    return fleets.filter((val) => val.name.toLowerCase().includes(t));
  }, [q, fleets]);

  const activeFleets = fleets.filter((f) => f.is_active).length;
  const hiddenFleets = fleets.filter((f) => !f.is_active).length;
  const totalVessels = fleets.reduce((acc, curr) => acc + curr.vessels.length, 0);

  return (
    <div className="w-full space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">Fleets Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your vessel groupings and fleet assignments
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat icon={Layers} label="Total Fleets" value={fleets.length} tone="primary" />
        <MiniStat icon={CheckCircle2} label="Active" value={activeFleets} tone="success" />
        <MiniStat icon={XCircle} label="Hidden" value={hiddenFleets} tone="warning" />
        <MiniStat icon={Ship} label="Assigned Vessels" value={totalVessels} tone="info" />
      </div>

      <Panel
        title={`Fleets List (${filtered.length})`}
        description="Search by fleet name"
        action={
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search fleets..."
                className="h-9 w-64 pl-8 pr-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-gray-900 dark:text-white"
              />
            </div>
            <button
              onClick={() => {
                setSelectedFleet(null);
                setOpenModalForm(true);
              }}
              className="h-9 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors"
            >
              Create
            </button>
          </div>
        }
      >
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2 pr-3 font-medium pl-2">Fleet Name</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Vessels Assigned</th>
                <th className="py-2 pr-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const isExpanded = !!expanded[f.id];
                return (
                  <React.Fragment key={f.id}>
                    <tr
                      className={cn(
                        "border-b border-gray-200/60 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer",
                        isExpanded && "bg-gray-50/30 dark:bg-gray-800/30"
                      )}
                      onClick={() => toggleExpand(f.id)}
                    >
                      <td className="py-3 pr-3 pl-2 flex items-center gap-2">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        <div className="font-medium text-gray-900 dark:text-white">{f.name}</div>
                      </td>
                      <td className="py-3 pr-3">
                        <StatusPill tone={f.is_active ? "success" : "danger"}>
                          {f.is_active ? "Active" : "Hidden"}
                        </StatusPill>
                      </td>
                      <td className="py-3 pr-3 text-gray-500 dark:text-gray-400">
                        {f.vessels.length} vessel{f.vessels.length !== 1 ? 's' : ''}
                      </td>
                      <td className="py-3 pr-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFleet(f);
                              setOpenModalForm(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded transition-colors"
                            title="Edit Fleet"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFleet(f.id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete Fleet"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {isExpanded && (
                      <tr className="border-b border-gray-200/60 dark:border-gray-800/60 bg-gray-50/50 dark:bg-gray-800/30">
                        <td colSpan={4} className="py-4 px-6 border-l-4 border-brand-500">
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1">
                            <Ship className="h-3 w-3" /> Vessels in this Fleet
                          </h4>
                          {f.vessels.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {f.vessels.map((v) => (
                                <div key={v.id} className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                                  <div className="h-6 w-6 rounded bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                                    <Ship className="h-3 w-3 text-brand-500" />
                                  </div>
                                  <div className="flex flex-col overflow-hidden">
                                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">{v.vessel_name}</span>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{v.id}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 dark:text-gray-400 italic">No vessels assigned to this fleet.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-500 dark:text-gray-400">
                    No fleets match &quot;{q}&quot;.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Modal modal={modalForm} setModal={setOpenModalForm} className="max-w-3xl max-h-full">
        <FleetForm
          fleetData={selectedFleet}
          closeForm={() => {
            setSelectedFleet(null);
            setOpenModalForm(false);
          }}
        />
      </Modal>
    </div>
  );
}
